/**
 * プリロード戦略ユーティリティ
 * リソースの事前読み込みとプリフェッチを管理
 */

type ResourceType = 'script' | 'style' | 'image' | 'font' | 'document' | 'fetch';
type LoadPriority = 'high' | 'medium' | 'low' | 'auto';

interface PreloadResource {
  url: string
  type: ResourceType
  priority?: LoadPriority
  crossOrigin?: 'anonymous' | 'use-credentials'
  media?: string
}

interface PreloadOptions {
  /** バッチサイズ（同時プリロード数） */
  batchSize?: number
  /** 遅延時間（ミリ秒） */
  delay?: number
  /** ネットワーク状態を考慮するか */
  respectNetworkStatus?: boolean
  /** デバッグモード */
  debug?: boolean
}

class PreloadManager {
  private preloadedUrls = new Set<string>()
  private pendingPreloads: PreloadResource[] = []
  private isProcessing = false
  private observer: PerformanceObserver | null = null

  constructor(private options: PreloadOptions = {}) {
    this.options = {
      batchSize: 3,
      delay: 100,
      respectNetworkStatus: true,
      debug: false,
      ...options
    }

    this.setupPerformanceObserver()
  }

  /**
   * リソースをプリロード
   */
  async preload(resources: PreloadResource | PreloadResource[]): Promise<void> {
    const resourceArray = Array.isArray(resources) ? resources : [resources]
    
    // 既にプリロード済みのURLをフィルタ
    const newResources = resourceArray.filter(r => !this.preloadedUrls.has(r.url))
    
    if (newResources.length === 0) {
      if (this.options.debug) {
        console.log('[Preload] All resources already preloaded')
      }
      return
    }

    // ネットワーク状態をチェック
    if (this.options.respectNetworkStatus && !this.shouldPreload()) {
      if (this.options.debug) {
        console.log('[Preload] Skipping due to network conditions')
      }
      this.pendingPreloads.push(...newResources)
      return
    }

    // バッチ処理
    await this.processBatch(newResources)
  }

  /**
   * プリフェッチ（次のページで使用予定のリソース）
   */
  prefetch(urls: string | string[]): void {
    const urlArray = Array.isArray(urls) ? urls : [urls]
    
    urlArray.forEach(url => {
      if (this.preloadedUrls.has(url)) return
      
      const link = document.createElement('link')
      link.rel = 'prefetch'
      link.href = url
      document.head.appendChild(link)
      
      this.preloadedUrls.add(url)
      
      if (this.options.debug) {
        console.log(`[Prefetch] Added prefetch hint for: ${url}`)
      }
    })
  }

  /**
   * DNSプリフェッチ
   */
  dnsPrefetch(origins: string | string[]): void {
    const originArray = Array.isArray(origins) ? origins : [origins]
    
    originArray.forEach(origin => {
      const link = document.createElement('link')
      link.rel = 'dns-prefetch'
      link.href = origin
      document.head.appendChild(link)
      
      if (this.options.debug) {
        console.log(`[DNS Prefetch] Added for: ${origin}`)
      }
    })
  }

  /**
   * プリコネクト（TCP接続の事前確立）
   */
  preconnect(origins: string | string[], crossOrigin?: 'anonymous' | 'use-credentials'): void {
    const originArray = Array.isArray(origins) ? origins : [origins]
    
    originArray.forEach(origin => {
      const link = document.createElement('link')
      link.rel = 'preconnect'
      link.href = origin
      if (crossOrigin) {
        link.crossOrigin = crossOrigin
      }
      document.head.appendChild(link)
      
      if (this.options.debug) {
        console.log(`[Preconnect] Established for: ${origin}`)
      }
    })
  }

  /**
   * 重要なリソースをプリロード（Highest Priority）
   */
  async preloadCritical(resources: PreloadResource[]): Promise<void> {
    const criticalResources = resources.map(r => ({
      ...r,
      priority: 'high' as LoadPriority
    }))
    
    // 即座に処理（バッチサイズ無視）
    await Promise.all(
      criticalResources.map(resource => this.createPreloadLink(resource))
    )
  }

  /**
   * バッチ処理
   */
  private async processBatch(resources: PreloadResource[]): Promise<void> {
    if (this.isProcessing) {
      this.pendingPreloads.push(...resources)
      return
    }

    this.isProcessing = true
    const { batchSize = 3, delay = 100 } = this.options

    // 優先度でソート
    const sorted = this.sortByPriority(resources)

    for (let i = 0; i < sorted.length; i += batchSize) {
      const batch = sorted.slice(i, i + batchSize)
      
      await Promise.all(
        batch.map(resource => this.createPreloadLink(resource))
      )

      // 次のバッチまで遅延
      if (i + batchSize < sorted.length) {
        await this.sleep(delay)
      }
    }

    this.isProcessing = false

    // ペンディングのリソースを処理
    if (this.pendingPreloads.length > 0) {
      const pending = [...this.pendingPreloads]
      this.pendingPreloads = []
      await this.processBatch(pending)
    }
  }

  /**
   * プリロードリンクを作成
   */
  private async createPreloadLink(resource: PreloadResource): Promise<void> {
    return new Promise((resolve) => {
      const link = document.createElement('link')
      link.rel = 'preload'
      link.as = resource.type
      link.href = resource.url
      
      if (resource.crossOrigin) {
        link.crossOrigin = resource.crossOrigin
      }
      
      if (resource.media) {
        link.media = resource.media
      }

      // fetchpriorityがサポートされている場合（'auto' | 'high' | 'low' のみ有効）
      if ('fetchPriority' in link && resource.priority && resource.priority !== 'auto') {
        const fetchPriority = resource.priority === 'medium' ? 'low' : resource.priority
        ;(link as HTMLLinkElement & { fetchPriority?: 'auto' | 'high' | 'low' }).fetchPriority = fetchPriority
      }

      link.onload = () => {
        this.preloadedUrls.add(resource.url)
        if (this.options.debug) {
          console.log(`[Preload] Loaded: ${resource.url}`)
        }
        resolve()
      }

      link.onerror = () => {
        console.error(`[Preload] Failed: ${resource.url}`)
        resolve() // エラーでも続行
      }

      document.head.appendChild(link)
    })
  }

  /**
   * ネットワーク状態に基づいてプリロードすべきか判断
   */
  private shouldPreload(): boolean {
    // Network Information APIがサポートされている場合
    if ('connection' in navigator) {
      const connection = (navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number; saveData?: boolean } }).connection

      // 低速接続や省データモードの場合はスキップ
      if (connection?.saveData) return false
      if (connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return false
    }

    // オフラインの場合はスキップ
    if (!navigator.onLine) return false

    return true
  }

  /**
   * 優先度でソート
   */
  private sortByPriority(resources: PreloadResource[]): PreloadResource[] {
    const priorityOrder: Record<LoadPriority, number> = {
      high: 0,
      medium: 1,
      low: 2,
      auto: 3
    }

    return [...resources].sort((a, b) => {
      const aPriority = priorityOrder[a.priority || 'auto']
      const bPriority = priorityOrder[b.priority || 'auto']
      return aPriority - bPriority
    })
  }

  /**
   * パフォーマンスオブザーバーのセットアップ
   */
  private setupPerformanceObserver(): void {
    if (!('PerformanceObserver' in window)) return

    try {
      this.observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'resource') {
            const resourceEntry = entry as PerformanceResourceTiming
            if (this.options.debug) {
              console.log(`[Performance] ${resourceEntry.name}: ${resourceEntry.duration.toFixed(2)}ms`)
            }
          }
        }
      })

      this.observer.observe({ entryTypes: ['resource'] })
    } catch (error) {
      console.error('[Preload] Failed to setup PerformanceObserver:', error)
    }
  }

  /**
   * スリープユーティリティ
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * クリーンアップ
   */
  destroy(): void {
    this.observer?.disconnect()
    this.preloadedUrls.clear()
    this.pendingPreloads = []
  }
}

// シングルトンインスタンス
let preloadManager: PreloadManager | null = null

/**
 * プリロードマネージャーを初期化
 */
export function initPreloadManager(options?: PreloadOptions): PreloadManager {
  if (!preloadManager) {
    preloadManager = new PreloadManager(options)
  }
  return preloadManager
}

/**
 * プリロードマネージャーを取得
 */
export function getPreloadManager(): PreloadManager | null {
  return preloadManager
}

/**
 * デモページ用のプリロード戦略
 */
export async function preloadDemoResources(demoName: string): Promise<void> {
  const manager = initPreloadManager()
  
  // 共通リソース
  await manager.preloadCritical([
    { url: '/assets/three/three-core.js', type: 'script', priority: 'high' },
    { url: '/assets/webgl/webgl-core.js', type: 'script', priority: 'high' }
  ])
  
  // デモ固有のリソース
  await manager.preload([
    { url: `/assets/demos/demo-${demoName.toLowerCase()}.js`, type: 'script', priority: 'medium' },
    { url: '/assets/shaders/shaders.js', type: 'script', priority: 'low' }
  ])
}

/**
 * 次のページリソースをプリフェッチ
 */
export function prefetchNextPage(urls: string[]): void {
  const manager = initPreloadManager()
  manager.prefetch(urls)
}

export default PreloadManager